import { useFormContext } from 'react-hook-form'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { ProcessType } from '../../../types/response'
import { EndpointRequest } from '../../../types/request'
import { getLocations, getSuppliers } from '../../../services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { CreateProcessFormData } from './index'
import CustomerPicker from '@/components/Form/Shared/CustomerPicker'

interface EndpointSelectorProps {
    processType: ProcessType
}

// Type to endpoint mapping
const getEndpointConfig = (processType: ProcessType) => {
    switch (processType) {
        case 'import_purchase':
            return { source: 'Supplier', destination: 'Location' }
        case 'import_return':
            return { source: 'Customer', destination: 'Location' }
        case 'export_sale':
            return { source: 'Location', destination: 'Customer' }
        case 'export_loan':
            return { source: 'Location', destination: 'Customer' }
        case 'export_delivery':
            return { source: 'Location', destination: 'Customer' }
        case 'internal_transfer':
            return { source: 'Location', destination: 'Location' }
        case 'inventory_check':
            return { source: 'Location', destination: null }
        default:
            return { source: 'Location', destination: 'Location' }
    }
}

const EndpointSelector = ({ processType }: EndpointSelectorProps) => {
    const {
        setValue,
        watch,
        formState: { errors },
        clearErrors,
    } = useFormContext<CreateProcessFormData>()
    const source = watch('source')
    const destination = watch('destination')

    const endpointConfig = getEndpointConfig(processType)

    // Fetch locations
    const { data: locationsData, isLoading: isLoadingLocations } =
        useQueryWithHandler(
            {
                queryKey: [QUERY_KEYS.WAREHOUSE_LOCATIONS],
                queryFn: () => getLocations({ page_size: 100 }),
            },
            { showLoading: false, showError: false }
        )

    // Fetch suppliers
    const { data: suppliersData, isLoading: isLoadingSuppliers } =
        useQueryWithHandler(
            {
                queryKey: [QUERY_KEYS.WAREHOUSE_SUPPLIERS],
                queryFn: () => getSuppliers({ page_size: 100 }),
            },
            { showLoading: false, showError: false }
        )

    const locations = locationsData?.results || []
    const suppliers = suppliersData?.results || []

    // Loading state for selects
    const isSourceLoading =
        (endpointConfig.source === 'Location' && isLoadingLocations) ||
        (endpointConfig.source === 'Supplier' && isLoadingSuppliers)
    const isDestinationLoading =
        endpointConfig.destination === 'Location' && isLoadingLocations

    const handleSourceChange = (value: string) => {
        const [type, id] = value.split(':')
        const endpoint: EndpointRequest = {
            type: type as 'Location' | 'Supplier' | 'Customer',
        }

        if (type === 'Location') {
            endpoint.location_id = id
        } else if (type === 'Supplier') {
            endpoint.supplier_id = id
        } else if (type === 'Customer') {
            endpoint.customer_id = parseInt(id)
        }

        setValue('source', endpoint)
        clearErrors('source')
    }

    const handleDestinationChange = (value: string) => {
        const [type, id] = value.split(':')
        const endpoint: EndpointRequest = {
            type: type as 'Location' | 'Supplier' | 'Customer',
        }

        if (type === 'Location') {
            endpoint.location_id = id
        } else if (type === 'Customer') {
            endpoint.customer_id = parseInt(id)
        }

        setValue('destination', endpoint)
        clearErrors('destination')
    }

    const getSourceValue = () => {
        if (!source) return undefined
        if (source.location_id) return `Location:${source.location_id}`
        if (source.supplier_id) return `Supplier:${source.supplier_id}`
        if (source.customer_id) return `Customer:${source.customer_id}`
        return undefined
    }

    const getDestinationValue = () => {
        if (!destination) return undefined
        if (destination.location_id)
            return `Location:${destination.location_id}`
        if (destination.customer_id)
            return `Customer:${destination.customer_id}`
        return undefined
    }

    const renderSourceOptions = () => {
        switch (endpointConfig.source) {
            case 'Location':
                return locations.map((loc) => (
                    <SelectItem key={loc.id} value={`Location:${loc.id}`}>
                        {loc.name} ({loc.code})
                    </SelectItem>
                ))
            case 'Supplier':
                return suppliers.map((supplier) => (
                    <SelectItem
                        key={supplier.id}
                        value={`Supplier:${supplier.id}`}
                    >
                        {supplier.name}
                    </SelectItem>
                ))
            default:
                return null
        }
    }

    const renderDestinationOptions = () => {
        switch (endpointConfig.destination) {
            case 'Location':
                return locations.map((loc) => (
                    <SelectItem key={loc.id} value={`Location:${loc.id}`}>
                        {loc.name} ({loc.code})
                    </SelectItem>
                ))
            default:
                return null
        }
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Source */}
            <div className="space-y-2" data-tour="warehouse-source-selector">
                <Label>
                    Source ({endpointConfig.source})
                    {endpointConfig.source && (
                        <span className="text-destructive ml-1">*</span>
                    )}
                </Label>
                {endpointConfig.source === 'Customer' ? (
                    <CustomerPicker
                        value={source?.customer_id}
                        onChange={(customerId) => {
                            setValue(
                                'source',
                                customerId
                                    ? {
                                          type: 'Customer',
                                          customer_id: customerId,
                                      }
                                    : undefined
                            )
                            clearErrors('source')
                        }}
                        placeholder="Select customer..."
                    />
                ) : (
                    <Select
                        key={`source-${locations.length}-${suppliers.length}-${getSourceValue() || 'none'}`}
                        value={getSourceValue()}
                        onValueChange={handleSourceChange}
                        disabled={isSourceLoading}
                    >
                        <SelectTrigger
                            className={
                                errors.source ? 'border-destructive' : ''
                            }
                        >
                            <SelectValue
                                placeholder={
                                    isSourceLoading
                                        ? 'Loading...'
                                        : `Select ${endpointConfig.source}...`
                                }
                            />
                        </SelectTrigger>
                        <SelectContent>{renderSourceOptions()}</SelectContent>
                    </Select>
                )}
                {errors.source && (
                    <p className="text-sm text-destructive">
                        {errors.source.message?.toString()}
                    </p>
                )}
            </div>

            {/* Destination */}
            {endpointConfig.destination && (
                <div
                    className="space-y-2"
                    data-tour="warehouse-destination-selector"
                >
                    <Label>
                        Destination ({endpointConfig.destination})
                        <span className="text-destructive ml-1">*</span>
                    </Label>
                    {endpointConfig.destination === 'Customer' ? (
                        <CustomerPicker
                            value={destination?.customer_id}
                            onChange={(customerId) => {
                                setValue(
                                    'destination',
                                    customerId
                                        ? {
                                              type: 'Customer',
                                              customer_id: customerId,
                                          }
                                        : undefined
                                )
                                clearErrors('destination')
                            }}
                            placeholder="Select customer..."
                        />
                    ) : (
                        <Select
                            key={`destination-${locations.length}-${getDestinationValue() || 'none'}`}
                            value={getDestinationValue()}
                            onValueChange={handleDestinationChange}
                            disabled={isDestinationLoading}
                        >
                            <SelectTrigger
                                className={
                                    errors.destination
                                        ? 'border-destructive'
                                        : ''
                                }
                            >
                                <SelectValue
                                    placeholder={
                                        isDestinationLoading
                                            ? 'Loading...'
                                            : `Select ${endpointConfig.destination}...`
                                    }
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {renderDestinationOptions()}
                            </SelectContent>
                        </Select>
                    )}
                    {errors.destination && (
                        <p className="text-sm text-destructive">
                            {errors.destination.message?.toString()}
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}

export default EndpointSelector
